import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowName } from "@/utils/automations";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { WorkflowCard } from "@/components/automations/AutomationCard";

type DeleteWorkflowDialogProps = {
    workflow: PlainWorkflow;
    open: boolean;
    onClose: () => void;
};

export const DeleteWorkflowDialog: FC<DeleteWorkflowDialogProps> = ({ workflow, open, onClose }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const deleteWorkflow = useDeleteWorkflow();

    const handleDelete = useCallback(async () => {
        try {
            await deleteWorkflow.mutateAsync(workflow.id);
            toast({
                title: "Automation deleted",
                description: `${workflowName(workflow)} has been deleted successfully.`,
            });
            onClose();
            navigate("/automations");
        } catch (error) {
            toast({
                title: "Failed to delete automation",
                description: formatError(error),
            });
        }
    }, [deleteWorkflow, workflow, toast, onClose, navigate]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="delete-workflow-dialog"
                data-track-location={TrackLocations.DeleteWorkflowDialog}
            >
                <DialogHeader>
                    <DialogTitle>Delete Workflow</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this automation? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <WorkflowCard workflow={workflow} readonly />
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="delete-workflow-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteWorkflow.isPending}
                        data-testid="delete-workflow-dialog-confirm"
                        data-tracking-id="confirm-delete-workflow-delete-workflow-dialog"
                    >
                        Delete Workflow
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
